/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

@file:Suppress("TooManyFunctions")

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.collections.immutable.persistentListOf
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import systems.frictionless.impulse.browser.ErrorTranslation
import systems.frictionless.impulse.browser.composable.component.CenteredOnScreen
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.InlinedErrorMessage
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.composable.component.LoginFormContainer
import systems.frictionless.impulse.browser.composable.component.PrivacyPolicyLink
import systems.frictionless.impulse.browser.composable.component.SideNavigationSection
import systems.frictionless.impulse.browser.composable.component.TextField
import systems.frictionless.impulse.browser.composable.component.Type
import systems.frictionless.impulse.browser.composable.component.WhiteBox
import systems.frictionless.impulse.browser.ext.id
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.routing.Route
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.browser.util.DO_NOTHING
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.model.Email
import systems.frictionless.impulse.rest.model.Login
import systems.frictionless.impulse.rest.model.LoginErrorCodes
import systems.frictionless.impulse.rest.model.Password
import systems.frictionless.impulse.rest.model.User
import systems.frictionless.impulse.viewmodel.LoginViewModel
import systems.frictionless.impulse.viewmodel.NetworkViewModelState

@Composable
fun WizardLoginSection(
    loginViewModel: LoginViewModel,
    onForgotPassword: () -> Unit,
    onSignedIn: (AuthenticatedUser) -> Unit
) {
    ContentContainer(
        navigationSection = SideNavigationSection.SignIn,
        mainContainerCssClass = AppStyleSheet.login,
        mainContentContainerId = HtmlId.SectionLogin,
        mainContent = {
            CenteredOnScreen(
                classes =
                    persistentListOf(
                        AppStyleSheet.ignoreCenteringOnMobileLandscape,
                        AppStyleSheet.addHorizontalPaddingInMobileLandscape
                    )
            ) {
                LoginForm(loginViewModel, onForgotPassword, onSignedIn)
            }
        }
    )
}

/**
 * Reusable composable to hook up login in multiple parts of the application.
 */
@Composable
fun LoginForm(
    loginViewModel: LoginViewModel,
    onForgotPassword: () -> Unit,
    onSignedIn: (AuthenticatedUser) -> Unit
) {
    /*
     * A little icky; need to retain nullable state to pass along to the next state.
     * While we tested attaching this to the states, which eliminated the null safety issues, it didn't feel right
     * from an API design standpoint.  Perhaps this should be attached to LoginViewModel instead
     *
     * In the meantime, storing the old values here, it better allows us to support a back button in the future since
     * the UI is holding onto the previous values.
     */
    val (prevEmail, setPrevEmail) = remember { mutableStateOf<Email?>(null) }
    val (prevBasicAuth, setPrevBasicAuth) = remember { mutableStateOf<Authentication.Form?>(null) }
    val (prevBearerAuth, setBearerAuth) = remember { mutableStateOf<Authentication.Bearer?>(null) }

    LoginFormContainer {
        when (val loginStateSnapshot = loginViewModel.state.collectAsState().value) {
            is LoginViewModel.State.Check -> {
                CheckEmailSection(loginStateSnapshot) {
                    setPrevEmail(it)
                    loginViewModel.transition()
                }
            }
            is LoginViewModel.State.SignUp ->
                SignUpSection(loginStateSnapshot, prevEmail!!) {
                    setPrevBasicAuth(it)
                    loginViewModel.transition()
                }
            is LoginViewModel.State.SignIn -> {
                val signInDetails =
                    prevBasicAuth?.let { SignInDetails.Full(it) } ?: SignInDetails.Partial(prevEmail!!)
                SignInSection(loginStateSnapshot, signInDetails, onForgotPassword) {
                    setBearerAuth(it)
                    loginViewModel.transition()
                }
            }
            is LoginViewModel.State.GetUser ->
                GetUserSection(loginStateSnapshot, prevBearerAuth!!) {
                    onSignedIn(it)
                }
        }
    }
}

@Composable
private fun CheckEmailSection(
    viewModel: LoginViewModel.State.Check,
    onComplete: (Email) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> EmailForm(null) { viewModel.startLoginCheck(composableScope, it) }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure ->
            EmailForm(ErrorTranslation.translate(state.failure)) {
                viewModel.startLoginCheck(
                    composableScope,
                    it
                )
            }
        is NetworkViewModelState.Complete -> {
            onComplete(state.result.email)
        }
    }
}

@Composable
private fun EmailForm(
    errorMessage: String?,
    onSubmit: (Login.Builder) -> Unit
) {
    val (email, setEmail) = remember { mutableStateOf("") }

    WhiteBox(classes = arrayOf(AppStyleSheet.narrowBox)) {
        LoginHeader("What is your email?")

        val submitForm = {
            onSubmit(Login.Builder(Email.new(email)))
        }

        Form(action = DO_NOTHING) {
            TextField(
                value = email,
                onValueChange = setEmail,
                type = Type.Email,
                onEnterKey = submitForm,
                autoFocus = true,
                maxLength = User.MAX_EMAIL_LENGTH,
                id = HtmlId.FormFieldEmail
            )
            errorMessage?.let {
                InlinedErrorMessage(it)
                Br()
            }
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    id(HtmlId.PrimaryButton)
                    if (!User.Builder.preflightEmail(email)) {
                        disabled()
                    }
                    onClick {
                        submitForm()
                    }
                }
            ) {
                Text("Next")
            }
            P {
                Br()
                Text(
                    "Your email is used to look up and secure your account. " +
                        "Information is handled according to our "
                )
                PrivacyPolicyLink()
                Text(".")
            }
        }
    }
}

@Composable
private fun LoginHeader(headerText: String) {
    H1(attrs = { id(HtmlId.Headline) }) {
        Text(headerText)
    }
}

@Composable
private fun SignUpSection(
    viewModel: LoginViewModel.State.SignUp,
    prevEmail: Email,
    onComplete: (Authentication.Form) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle ->
            SignUpForm(null, prevEmail, null) {
                viewModel.startSignUp(
                    composableScope,
                    it
                )
            }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure ->
            SignUpForm(ErrorTranslation.translate(state.failure), prevEmail, null) {
                // Note: I think form state is lost here; might need to hoist first/last/password into SignUpSection
                viewModel.startSignUp(composableScope, it)
            }
        is NetworkViewModelState.Complete -> {
            onComplete(state.result)
        }
    }
}

@Composable
@Suppress("LongMethod")
private fun SignUpForm(
    errorMessage: String?,
    prevEmail: Email,
    userBuilder: User.Builder?,
    action: (User.Builder) -> Unit
) {
    val (firstName, setFirstName) = remember { mutableStateOf(userBuilder?.firstName ?: "") }
    val (lastName, setLastName) = remember { mutableStateOf(userBuilder?.lastName ?: "") }
    val (password, setPassword) = remember { mutableStateOf(userBuilder?.password?.plaintext ?: "") }
    val submitForm = {
        action(User.Builder(firstName, lastName, prevEmail, Password(password)))
    }

    WhiteBox(classes = arrayOf(AppStyleSheet.narrowBox)) {
        LoginHeader("Enter your details")
        Form(action = DO_NOTHING) {
            TextField(
                value = firstName,
                onValueChange = setFirstName,
                label = { Text("First name") },
                type = Type.FirstName,
                onEnterKey = submitForm,
                autoFocus = true,
                maxLength = User.MAX_FIRST_NAME_LENGTH,
                id = HtmlId.FormFieldFirstName
            )
            TextField(
                value = lastName,
                onValueChange = setLastName,
                label = { Text("Last name") },
                type = Type.LastName,
                onEnterKey = submitForm,
                maxLength = User.MAX_LAST_NAME_LENGTH,
                id = HtmlId.FormFieldLastName
            )
            TextField(
                value = password,
                onValueChange = setPassword,
                label = { Text("Password") },
                type = Type.NewPassword,
                onEnterKey = submitForm,
                maxLength = Password.MAX_PASSWORD_LENGTH,
                id = HtmlId.FormFieldPassword
            )

            errorMessage?.let {
                InlinedErrorMessage(it)
            }

            Div {
                Button(
                    attrs = {
                        classes(AppStyleSheet.blueButton)
                        id(HtmlId.PrimaryButton)
                        if (!(
                                User.Builder.preflightFirstName(firstName) &&
                                    User.Builder.preflightLastName(lastName) &&
                                    // In the future, we can look at the enum values to give error messages to the user
                                    Password.validate(password).isEmpty()
                            )
                        ) {
                            disabled()
                        }
                        onClick {
                            submitForm()
                        }
                    }
                ) {
                    Text("Next")
                }
            }
        }
    }
}

sealed class SignInDetails {
    data class Partial(val email: Email) : SignInDetails()

    data class Full(val form: Authentication.Form) : SignInDetails()
}

@Composable
private fun SignInSection(
    viewModel: LoginViewModel.State.SignIn,
    prevSignInDetails: SignInDetails,
    onForgotPassword: () -> Unit,
    onComplete: (Authentication.Bearer) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            when (prevSignInDetails) {
                is SignInDetails.Full -> {
                    LoginProgress()
                    viewModel.startSignIn(composableScope, prevSignInDetails.form)
                }
                is SignInDetails.Partial ->
                    SignInForm(
                        null,
                        prevSignInDetails.email,
                        onForgotPassword
                    ) { viewModel.startSignIn(composableScope, it) }
            }
        }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure -> {
            val errorMessage =
                ErrorTranslation.translate(state.failure) {
                    when (it.serverError.errorCode) {
                        LoginErrorCodes.UNAUTHORIZED -> "Password is incorrect"
                        else -> it.toString() // Need more translations for other error types
                    }
                }
            when (prevSignInDetails) {
                /*
                 * For the full case, it is probably better to show a different retry screen rather than ask the user
                 * to type their password again.  Although this is good enough for an MVP.  This should only occur
                 * if there's a network error, which we hope would be rare.
                 *
                 * Analytics would also be useful to see how frequently this corner case is hit.  Although if
                 * there are network issues then the analytics data would also likely be incomplete.
                 */

                is SignInDetails.Full ->
                    SignInForm(errorMessage, prevSignInDetails.form.email, onForgotPassword) {
                        viewModel.startSignIn(
                            composableScope,
                            it
                        )
                    }
                is SignInDetails.Partial ->
                    SignInForm(errorMessage, prevSignInDetails.email, onForgotPassword) {
                        viewModel.startSignIn(
                            composableScope,
                            it
                        )
                    }
            }
        }
        is NetworkViewModelState.Complete -> {
            onComplete(state.result)
        }
    }
}

@Composable
fun SignInForm(
    errorMessage: String?,
    prevEmail: Email,
    @Suppress("UNUSED_PARAMETER") onForgotPassword: () -> Unit,
    action: (Authentication.Form) -> Unit
) {
    val (password, setPassword) = remember { mutableStateOf("") }

    WhiteBox(classes = arrayOf(AppStyleSheet.narrowBox)) {
        LoginHeader("Sign into your account")

        val submitForm = {
            action(Authentication.Form(prevEmail, password))
        }

        Form(action = DO_NOTHING) {
            TextField(
                value = prevEmail.emailString,
                label = { Text("Email") },
                type = Type.Email,
                onEnterKey = submitForm,
                maxLength = User.MAX_EMAIL_LENGTH,
                id = HtmlId.FormFieldEmail
            )
            TextField(
                value = password,
                onValueChange = setPassword,
                label = { Text("Password") },
                type = Type.Password,
                onEnterKey = submitForm,
                autoFocus = true,
                maxLength = Password.MAX_PASSWORD_LENGTH,
                id = HtmlId.FormFieldPassword
            )

            errorMessage?.let {
                InlinedErrorMessage(it)
            }

            Div {
                Button(
                    attrs = {
                        classes(AppStyleSheet.blueButton)
                        id(HtmlId.PrimaryButton)
                        // In the future, we can use the enum set values to give hints to the user.
                        if ((Password.validate(password)).isNotEmpty()) {
                            disabled()
                        }
                        onClick {
                            submitForm()
                        }
                    }
                ) {
                    Text("Next")
                }
            }

            P {
                A(
                    href = Route.ForgotPassword.toLink(),
                    attrs = { id(HtmlId.LinkForgotPassword) }
                ) { Text("Forgot password?") }
            }
        }
    }
}

@Composable
private fun GetUserSection(
    viewModel: LoginViewModel.State.GetUser,
    prevBearerAuth: Authentication.Bearer,
    onComplete: (AuthenticatedUser) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            LoginProgress()
            viewModel.startGetUser(composableScope, prevBearerAuth)
        }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure ->
            GetUserForm(ErrorTranslation.translate(state.failure)) {
                viewModel.startGetUser(composableScope, prevBearerAuth)
            }
        is NetworkViewModelState.Complete -> {
            onComplete(state.result)
        }
    }
}

@Composable
private fun GetUserForm(
    errorMessage: String,
    action: () -> Unit
) {
    Div {
        InlinedErrorMessage(errorMessage)

        Div {
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    id(HtmlId.PrimaryButton)
                    onClick {
                        action()
                    }
                }
            ) {
                Text("Next")
            }
        }
    }
}

@Composable
private fun LoginProgress() {
    LoadingIndicator()
}
